import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';

const Hero = () => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ patients: 0, doctors: 0 });
  const [isVisible, setIsVisible] = useState(false);

  // Animated counter effect
  useEffect(() => {
    setIsVisible(true);
    const duration = 2000; // 2 seconds
    const steps = 60;
    const interval = duration / steps;
    
    const patientsTarget = 10000;
    const doctorsTarget = 500;
    
    let currentStep = 0;
    
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      setCounts({
        patients: Math.floor(patientsTarget * progress),
        doctors: Math.floor(doctorsTarget * progress)
      });
      
      if (currentStep >= steps) {
        clearInterval(timer);
        setCounts({ patients: patientsTarget, doctors: doctorsTarget });
      }
    }, interval);
    
    return () => clearInterval(timer);
  }, []);

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  return (
    <section style={styles.hero} className="hero-section">
      {/* Animated Background Elements */}
      <div style={styles.backgroundPattern}>
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.floatingCircle,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${20 + Math.random() * 60}px`,
              height: `${20 + Math.random() * 60}px`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${10 + Math.random() * 10}s`
            }}
          />
        ))}
      </div>

      <div style={styles.container} className="hero-container">
        <div style={styles.content}>
          {/* Badge */}
          <div style={{
            ...styles.badge,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease-out'
          }}>
            <span style={styles.badgeDot}></span>
            <span>Trusted by 10,000+ Patients Worldwide</span>
          </div>

          {/* Main Headline */}
          <h1 className="hero-title" style={{
            ...styles.title,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out 0.2s'
          }}>
            Your Health,
            <br />
            <span style={styles.titleGradient}>Our Priority</span>
          </h1>

          {/* Subtitle */}
          <p className="hero-subtitle" style={{
            ...styles.subtitle,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out 0.4s'
          }}>
            Connect with Top Specialists Instantly.
            <br />
            Professional Healthcare, Delivered to You.
          </p>

          {/* CTA Buttons */}
          <div className="hero-cta-container" style={{
            ...styles.ctaContainer,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out 0.6s'
          }}>
            <button
              style={styles.primaryBtn}
              onClick={() => navigate('/consult')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05) translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 16px 48px rgba(40, 108, 255, 0.4), 0 0 0 4px rgba(40, 108, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1) translateY(0)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(40, 108, 255, 0.3)';
              }}
            >
              <span>Book Consultation</span>
              <ArrowRight size={20} />
            </button>
            <button
              style={styles.secondaryBtn}
              onClick={() => navigate('/about')}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.transform = 'scale(1.05) translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1) translateY(0)';
              }}
            >
              <Play size={18} fill="white" />
              <span>Learn More</span>
            </button>
          </div>

          {/* Animated Stats */}
          <div className="hero-stats" style={{
            ...styles.stats,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out 0.8s'
          }}>
            <div style={styles.stat} className="hero-stat">
              <div style={styles.statNumber}>{formatNumber(counts.patients)}+</div>
              <div style={styles.statLabel}>Patients Served</div>
            </div>
            <div style={styles.statDivider}></div>
            <div style={styles.stat} className="hero-stat">
              <div style={styles.statNumber}>{formatNumber(counts.doctors)}+</div>
              <div style={styles.statLabel}>Expert Doctors</div>
            </div>
            <div style={styles.statDivider}></div>
            <div style={styles.stat} className="hero-stat">
              <div style={styles.statNumber}>24/7</div>
              <div style={styles.statLabel}>Available</div>
            </div>
          </div>
        </div>

        {/* Hero Illustration */}
        <div style={{
          ...styles.imageContainer,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateX(0) scale(1)' : 'translateX(50px) scale(0.95)',
          transition: 'all 1s ease-out 0.4s'
        }}>
          <div style={styles.imageWrapper}>
            {/* Heart-shaped family illustration SVG */}
            <svg viewBox="0 0 400 400" style={styles.illustration}>
              {/* Background heart shape with gradient */}
              <defs>
                <linearGradient id="heartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#5B8BFF', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#286CFF', stopOpacity: 1 }} />
                </linearGradient>
                <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#286CFF" floodOpacity="0.3"/>
                </filter>
              </defs>
              
              {/* Main heart shape */}
              <path
                d="M200 350 C 200 350, 80 250, 80 170 C 80 130, 100 100, 140 100 C 170 100, 190 120, 200 150 C 210 120, 230 100, 260 100 C 300 100, 320 130, 320 170 C 320 250, 200 350, 200 350 Z"
                fill="url(#heartGradient)"
                filter="url(#shadow)"
              />
              
              {/* Shield/Protection overlay */}
              <path
                d="M200 140 L190 145 C 190 145, 150 165, 150 200 C 150 235, 175 265, 200 280 C 225 265, 250 235, 250 200 C 250 165, 210 145, 210 145 L200 140 Z"
                fill="rgba(255, 255, 255, 0.15)"
                stroke="rgba(255, 255, 255, 0.3)"
                strokeWidth="2"
              />
              
              {/* Medical cross on shield */}
              <g>
                <rect x="197" y="175" width="6" height="30" fill="white" rx="2" />
                <rect x="185" y="187" width="30" height="6" fill="white" rx="2" />
              </g>
              
              {/* Family silhouettes inside heart */}
              <g transform="translate(200, 230)">
                {/* Father */}
                <circle cx="-25" cy="-30" r="15" fill="#FFF" opacity="0.95" />
                <rect x="-35" y="-10" width="20" height="30" rx="4" fill="#FFF" opacity="0.95" />
                
                {/* Mother */}
                <circle cx="15" cy="-30" r="15" fill="#FFF" opacity="0.95" />
                <rect x="5" y="-10" width="20" height="30" rx="4" fill="#FFF" opacity="0.95" />
                
                {/* Boy child */}
                <circle cx="-15" cy="5" r="10" fill="#FFE4B5" opacity="0.95" />
                <rect x="-22" y="18" width="14" height="20" rx="3" fill="#FFE4B5" opacity="0.95" />
                
                {/* Girl child */}
                <circle cx="15" cy="5" r="10" fill="#FFE4B5" opacity="0.95" />
                <rect x="8" y="18" width="14" height="20" rx="3" fill="#FFE4B5" opacity="0.95" />
              </g>
              
              {/* Floating healthcare icons around heart */}
              {/* Stethoscope */}
              <g transform="translate(100, 150)">
                <circle cx="0" cy="0" r="20" fill="white" opacity="0.9" />
                <path d="M-5 -8 Q 0 -12 5 -8 L 5 0 C 5 5 0 8 0 8 C 0 8 -5 5 -5 0 Z" stroke="#286CFF" strokeWidth="2" fill="none" />
              </g>
              
              {/* Heart rate */}
              <g transform="translate(300, 150)">
                <circle cx="0" cy="0" r="20" fill="white" opacity="0.9" />
                <path d="M-10 0 L -5 0 L -2 -8 L 2 8 L 5 0 L 10 0" stroke="#FF6B6B" strokeWidth="2" fill="none" />
              </g>
              
              {/* Calendar */}
              <g transform="translate(120, 280)">
                <circle cx="0" cy="0" r="18" fill="white" opacity="0.9" />
                <rect x="-8" y="-6" width="16" height="12" rx="2" stroke="#28C08A" strokeWidth="1.5" fill="none" />
                <line x1="-8" y1="-2" x2="8" y2="-2" stroke="#28C08A" strokeWidth="1.5" />
              </g>
              
              {/* Mobile phone */}
              <g transform="translate(280, 280)">
                <circle cx="0" cy="0" r="18" fill="white" opacity="0.9" />
                <rect x="-5" y="-8" width="10" height="16" rx="2" stroke="#FFB800" strokeWidth="1.5" fill="none" />
                <circle cx="0" cy="6" r="1.5" fill="#FFB800" />
              </g>
              
              {/* Document */}
              <g transform="translate(80, 220)">
                <circle cx="0" cy="0" r="16" fill="white" opacity="0.85" />
                <rect x="-6" y="-7" width="12" height="14" rx="1" stroke="#5B8BFF" strokeWidth="1.5" fill="none" />
                <line x1="-4" y1="-3" x2="4" y2="-3" stroke="#5B8BFF" strokeWidth="1" />
                <line x1="-4" y1="1" x2="4" y2="1" stroke="#5B8BFF" strokeWidth="1" />
              </g>
              
              {/* Security lock */}
              <g transform="translate(320, 220)">
                <circle cx="0" cy="0" r="16" fill="white" opacity="0.85" />
                <rect x="-4" y="-2" width="8" height="6" rx="1" fill="#286CFF" />
                <path d="M -3 -2 L -3 -5 Q -3 -7 0 -7 Q 3 -7 3 -5 L 3 -2" stroke="#286CFF" strokeWidth="1.5" fill="none" />
              </g>
            </svg>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div style={styles.scrollIndicator}>
        <div style={styles.scrollDot}></div>
      </div>
    </section>
  );
};

const styles = {
  hero: {
    position: 'relative',
    background: 'linear-gradient(135deg, #286CFF 0%, #1E5ABF 50%, #286CFF 100%)',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    padding: '120px 0 100px'
  },
  backgroundPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    opacity: 0.1
  },
  floatingCircle: {
    position: 'absolute',
    borderRadius: '50%',
    background: 'white',
    animation: 'float 15s infinite ease-in-out'
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '40px 32px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1
  },
  '@media (max-width: 1024px)': {
    container: {
      gridTemplateColumns: '1fr',
      gap: '48px',
      textAlign: 'center'
    }
  },
  content: {
    color: 'white'
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '24px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '32px'
  },
  badgeDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#28C08A',
    animation: 'pulse 2s infinite'
  },
  title: {
    fontSize: '72px',
    fontWeight: '900',
    lineHeight: '1.1',
    marginBottom: '28px',
    letterSpacing: '-2px'
  },
  titleGradient: {
    background: 'linear-gradient(135deg, #FFB800 0%, #FFA500 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    textShadow: '0 0 40px rgba(255, 184, 0, 0.3)'
  },
  subtitle: {
    fontSize: '22px',
    lineHeight: '1.7',
    marginBottom: '48px',
    color: 'rgba(255, 255, 255, 0.9)',
    maxWidth: '540px'
  },
  ctaContainer: {
    display: 'flex',
    gap: '20px',
    marginBottom: '56px',
    flexWrap: 'wrap'
  },
  primaryBtn: {
    background: 'white',
    color: '#286CFF',
    border: 'none',
    borderRadius: '16px',
    padding: '18px 40px',
    fontSize: '18px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 12px 32px rgba(40, 108, 255, 0.3)',
    letterSpacing: '0.3px'
  },
  secondaryBtn: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '16px',
    padding: '16px 36px',
    fontSize: '18px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    backdropFilter: 'blur(10px)'
  },
  stats: {
    display: 'flex',
    gap: '0',
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    padding: '28px 0',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
  },
  stat: {
    flex: 1,
    textAlign: 'center',
    padding: '0 24px'
  },
  statNumber: {
    fontSize: '36px',
    fontWeight: '800',
    marginBottom: '8px',
    background: 'linear-gradient(135deg, #FFB800, #FFA500)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent'
  },
  statLabel: {
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.8)',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '1px'
  },
  statDivider: {
    width: '1px',
    background: 'rgba(255, 255, 255, 0.2)'
  },
  imageContainer: {
    position: 'relative'
  },
  imageWrapper: {
    position: 'relative',
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(20px)',
    borderRadius: '32px',
    padding: '40px',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)'
  },
  illustration: {
    width: '100%',
    height: 'auto',
    display: 'block'
  },
  scrollIndicator: {
    position: 'absolute',
    bottom: '50px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 2
  },
  scrollDot: {
    width: '32px',
    height: '48px',
    border: '3px solid rgba(255, 255, 255, 0.5)',
    borderRadius: '16px',
    position: 'relative',
    '::before': {
      content: '""',
      position: 'absolute',
      top: '8px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      background: 'white',
      animation: 'scroll 2s infinite'
    }
  }
};

// Add keyframe animations
const styleSheet = document.styleSheets[0];
if (styleSheet) {
  try {
    styleSheet.insertRule(`
      @keyframes float {
        0%, 100% { transform: translateY(0) rotate(0deg); }
        50% { transform: translateY(-20px) rotate(180deg); }
      }
    `, styleSheet.cssRules.length);
    
    styleSheet.insertRule(`
      @keyframes pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(1.2); }
      }
    `, styleSheet.cssRules.length);
    
    styleSheet.insertRule(`
      @keyframes scroll {
        0% { opacity: 0; transform: translateX(-50%) translateY(0); }
        50% { opacity: 1; }
        100% { opacity: 0; transform: translateX(-50%) translateY(20px); }
      }
    `, styleSheet.cssRules.length);
  } catch (e) {
    // Ignore if rules already exist
  }
}

export default Hero;